import { Accordion, AccordionDetails, AccordionSummary, styled } from '@mui/material';
import { omitForwardedProps } from 'common/utils';

type WithSizeProp = {
  size: 'small' | 'medium';
};

export type ExpandableBoxVariants = 'text' | 'outlined';

export const ExpandableBoxWrapper = styled(Accordion, {
  shouldForwardProp: omitForwardedProps('boxVariant', 'isRounded'),
})<{ boxVariant: ExpandableBoxVariants; isRounded: boolean }>(({ theme: { shape }, isRounded, boxVariant }) => ({
  '&.MuiPaper-root': {
    borderRadius: isRounded ? shape.borderRadius * 2 : shape.borderRadius,
    ...(boxVariant === 'text' && {
      '&.MuiPaper-root': {
        boxShadow: 'none',
      },
      '& .MuiAccordionSummary-root': {
        padding: 0,
      },
    }),
    '&.Mui-disabled': {
      backgroundColor: 'initial',
      '& .MuiAccordionSummary-root': {
        opacity: 1,
      },
    },
  },
  '& .MuiAccordionSummary-root.Mui-focusVisible': {
    backgroundColor: 'initial',
  },
}));

type SummaryProps = WithSizeProp & {
  isExpanded: boolean;
  showDivider: boolean;
  expandIconPlacement: 'left' | 'right';
};

export const ExpandableBoxSummary = styled(AccordionSummary, {
  shouldForwardProp: omitForwardedProps<SummaryProps>('size', 'isExpanded', 'showDivider', 'expandIconPlacement'),
})<WithSizeProp & SummaryProps>(
  ({ theme: { spacing, typography }, size, isExpanded, showDivider, expandIconPlacement }) => ({
    padding: size === 'small' ? spacing(2) : spacing(3),
    paddingBottom: showDivider || !isExpanded ? undefined : 0,
    flexDirection: expandIconPlacement === 'left' ? 'row-reverse' : 'row',
    minHeight: 'unset',
    gap: spacing(1),
    alignItems: 'start',

    '& .MuiAccordionSummary-content': {
      ...typography['natter-text-xl'],

      margin: 0,
      gap: spacing(2),
      minWidth: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: 'inherit',

      '&.Mui-expanded': {
        transform: 'rotate(90deg)',
      },

      '@media print': {
        display: 'none',
      },
    },

    ...(isExpanded && {
      transition: 'padding 0.2s',
    }),
  })
);

export const ExpandableBoxContent = styled(AccordionDetails, {
  shouldForwardProp: omitForwardedProps('size', 'showDivider', 'variant'),
})<{ showDivider: boolean; variant: ExpandableBoxVariants } & WithSizeProp>(
  ({ theme: { spacing, palette }, size, showDivider, variant }) => ({
    whiteSpace: 'pre-wrap',
    padding: size === 'small' ? spacing(2) : spacing(3),
    ...(showDivider && {
      borderTop: '1px solid',
      borderColor: palette.divider,
    }),
    ...(variant === 'text' && {
      padding: 0,
    }),
  })
);

type ExpandableBoxAlertsProps = {
  expandIconPlacement: 'left' | 'right';
  actionsWidth: number;
};

export const ExpandableBoxAlerts = styled('div', {
  shouldForwardProp: omitForwardedProps<ExpandableBoxAlertsProps>('expandIconPlacement', 'actionsWidth'),
})<ExpandableBoxAlertsProps>(({ theme: { spacing }, expandIconPlacement, actionsWidth = 0 }) => ({
  marginTop: spacing(2),
  marginRight: -((actionsWidth ? 16 + actionsWidth : 0) + (expandIconPlacement === 'right' ? 28 : 0)),
  marginLeft: expandIconPlacement === 'left' ? -28 : 0,

  '&:empty': {
    display: 'none',
  },
}));
